// PRD §7.4.2 + §7.4.3 — mentor-facing company detail page.
//
// Shows the company blurb + open missions. If the company has zero
// real missions in ('active','draft_pending_founder'), the backend
// returns ONE Favcy-suggested mission derived from the company's
// Viren brief. The suggested card is read-only structurally (no real
// mission to engage on yet) BUT carries an "I'd be keen if the founder
// posted this" CTA so mentor appetite is captured and the founder gets
// nudged to post the real mission. PRD §7.4.3.
import { useEffect, useState } from "react";
import { useRoute, useLocation, Link } from "wouter";
import { ArrowLeft, ArrowRight, Sparkles, Info, Check, BookOpen, Lightbulb, RefreshCw } from "lucide-react";
import ProgressLoader from "@/components/progress-loader";

const BASE = import.meta.env.BASE_URL;

interface RealMission {
  kind: "real";
  id: number;
  title: string;
  status: string;
  domainTags: string[];
  authorKind: string;
}
interface SuggestedMission {
  kind: "favcy_suggestion";
  title: string;
  whatWeWillRun: string;
  successLooksLike: string;
  whyNow: string;
}
type MissionRow = RealMission | SuggestedMission;

interface Data {
  company: { id: number; name: string; description: string; sector: string | null; stage: string | null };
  missions: MissionRow[];
  suggestionSourceNote: string | null;
  myExpressedSuggestionTitles: string[];
}

export default function CompanyDetail() {
  const [, params] = useRoute("/mentor/companies/:id");
  const [, setLocation] = useLocation();
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showMissions, setShowMissions] = useState(false);
  // Per-suggestion submit state. Keyed by suggestion title since suggestions
  // have no id (read-only structural guarantee — see PRD §7.4.3 / GET route).
  const [submittingTitle, setSubmittingTitle] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // PRD §7.4.4 — symmetric proposal form state.
  const [showProposeForm, setShowProposeForm] = useState(false);
  const [proposeForm, setProposeForm] = useState({ title: "", problem: "", approach: "" });
  const [proposing, setProposing] = useState(false);
  const [proposeError, setProposeError] = useState<string | null>(null);
  const [proposeOutcome, setProposeOutcome] = useState<{ kind: "refined"; title: string; rationale: string | null } | { kind: "rejected"; reason: string } | null>(null);
  // Viren-seeded inspirations + read-only company context.
  type Idea = { title: string; problem: string; approach: string };
  type Ctx =
    | { ok: true; ready: true; companyExpertise: string | null; proofSuggestions: Array<{ title: string; whatItProves: string; whyItMattersForYou: string }>; missionSuggestions: Array<{ title: string; whatWeWillRun: string; whyNow: string }>; inputsUsed: string | null; generatedAt: string | null }
    | { ok: true; ready: false; status: string; message: string };
  const [showContext, setShowContext] = useState(false);
  const [context, setContext] = useState<Ctx | null>(null);
  const [contextErr, setContextErr] = useState<string | null>(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [ideas, setIdeas] = useState<Idea[] | null>(null);
  const [ideasErr, setIdeasErr] = useState<string | null>(null);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [ideasCached, setIdeasCached] = useState(false);

  useEffect(() => {
    if (!params?.id) return;
    const tok = localStorage.getItem("mentor_token") || "";
    fetch(`${BASE}api/mentor-portal/companies/${params.id}`, { headers: { "x-mentor-token": tok } })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.error || `${r.status}`)))
      .then(setData)
      .catch(e => setErr(String(e)));
  }, [params?.id]);

  async function loadContext() {
    if (!params?.id || contextLoading || context) return;
    setContextLoading(true); setContextErr(null);
    try {
      const tok = localStorage.getItem("mentor_token") || "";
      const r = await fetch(`${BASE}api/mentor-portal/companies/${params.id}/context`, { headers: { "x-mentor-token": tok } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `${r.status}`);
      setContext(j);
    } catch (e: any) {
      setContextErr(e?.message || String(e));
    } finally {
      setContextLoading(false);
    }
  }

  async function loadIdeas(refresh: boolean) {
    if (!params?.id || ideasLoading) return;
    setIdeasLoading(true); setIdeasErr(null);
    try {
      const tok = localStorage.getItem("mentor_token") || "";
      const r = await fetch(`${BASE}api/mentor-portal/companies/${params.id}/inspire-missions`, {
        method: "POST",
        headers: { "x-mentor-token": tok, "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `${r.status}`);
      setIdeas(j.ideas);
      setIdeasCached(Boolean(j.cached));
    } catch (e: any) {
      setIdeasErr(e?.message || String(e));
    } finally {
      setIdeasLoading(false);
    }
  }

  function useIdeaAsStartingPoint(i: Idea) {
    setProposeForm({ title: i.title, problem: i.problem, approach: i.approach });
    setShowProposeForm(true);
    setProposeOutcome(null);
    setProposeError(null);
    // Scroll the form into view so the mentor sees their pre-filled draft.
    setTimeout(() => {
      document.querySelector('[data-testid="form-propose-mission"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  async function expressSuggestedInterest(s: SuggestedMission) {
    if (!params?.id || !data) return;
    setSubmittingTitle(s.title);
    setSubmitError(null);
    const tok = localStorage.getItem("mentor_token") || "";
    try {
      const r = await fetch(`${BASE}api/mentor-portal/companies/${params.id}/suggested-interests`, {
        method: "POST",
        headers: { "x-mentor-token": tok, "Content-Type": "application/json" },
        body: JSON.stringify({
          title: s.title, whatWeWillRun: s.whatWeWillRun,
          successLooksLike: s.successLooksLike, whyNow: s.whyNow,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `${r.status}`);
      // Optimistic local update — push the title into the expressed list so
      // the card immediately swaps to the "Expressed" state without a refetch.
      setData({
        ...data,
        myExpressedSuggestionTitles: Array.from(new Set([...data.myExpressedSuggestionTitles, s.title])),
      });
    } catch (e: any) {
      setSubmitError(e?.message || String(e));
    } finally {
      setSubmittingTitle(null);
    }
  }

  async function submitProposal(e: React.FormEvent) {
    e.preventDefault();
    if (!params?.id || proposing) return;
    setProposing(true); setProposeError(null); setProposeOutcome(null);
    const tok = localStorage.getItem("mentor_token") || "";
    try {
      const r = await fetch(`${BASE}api/mentor-portal/companies/${params.id}/proposed-missions`, {
        method: "POST",
        headers: { "x-mentor-token": tok, "Content-Type": "application/json" },
        body: JSON.stringify(proposeForm),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `${r.status}`);
      if (j.virenStatus === "rejected_by_viren") {
        setProposeOutcome({ kind: "rejected", reason: j.virenRejectionReason || "Viren held this back." });
      } else if (j.virenStatus === "refined") {
        setProposeOutcome({ kind: "refined", title: j.refined?.title || proposeForm.title, rationale: j.refined?.rationale ?? null });
        setProposeForm({ title: "", problem: "", approach: "" });
      }
    } catch (e: any) {
      setProposeError(e?.message || String(e));
    } finally {
      setProposing(false);
    }
  }

  if (err) return (
    <div className="max-w-3xl mx-auto px-6 py-10 text-sm text-red-400" data-testid="text-company-detail-error">{err}</div>
  );
  if (!data) return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <ProgressLoader active={true} title="Loading" stages={["Loading company", "Pulling open missions", "Checking Favcy suggestions"]} />
    </div>
  );

  const c = data.company;
  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <button onClick={() => setLocation("/mentor")} className="text-xs text-[var(--mentor-fg-muted)] hover:text-[var(--mentor-fg)] inline-flex items-center gap-1 mb-4" data-testid="button-back-to-browse">
        <ArrowLeft className="w-3 h-3" /> Back to browse
      </button>

      {/* Company header */}
      <div className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-6 mb-4">
        <h1 className="text-2xl font-bold mb-1" data-testid="text-company-name">{c.name}</h1>
        <div className="text-xs text-[var(--mentor-fg-muted)] mb-4">
          {[c.sector, c.stage].filter(Boolean).join(" · ") || "Stage not set"}
        </div>
        <p className="text-sm text-[var(--mentor-fg)] leading-relaxed whitespace-pre-line" data-testid="text-company-description">
          {c.description?.trim() || <span className="text-[var(--mentor-fg-muted)] italic">No description on file yet.</span>}
        </p>
      </div>

      {/* Read-only company context — Viren's brief on this company.
          Collapsed by default to keep the page calm. Loads on first open
          so we don't pay for a brief lookup the mentor never asked for. */}
      <div className="mb-6">
        <button
          onClick={() => { const next = !showContext; setShowContext(next); if (next) loadContext(); }}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-lg bg-[var(--mentor-card)] border border-[var(--mentor-border)] hover:border-[#FF5C00]/40 transition text-left"
          data-testid="button-toggle-context"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold">
            <BookOpen className="w-4 h-4 text-[var(--mentor-accent)]" />
            Learn more about this company
          </span>
          <span className="text-xs text-[var(--mentor-fg-muted)]">{showContext ? "Hide" : "Show"} Viren's brief</span>
        </button>
        {showContext && (
          <div className="mt-3 bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-4 space-y-4" data-testid="section-context">
            {contextLoading && (
              <ProgressLoader active={true} title="Loading Viren's brief" stages={["Reading the company snapshot", "Pulling Viren's expertise notes"]} />
            )}
            {contextErr && (
              <div className="text-xs text-red-400" data-testid="text-context-error">Couldn't load context — {contextErr}</div>
            )}
            {context && context.ready === false && (
              <div className="text-xs text-[var(--mentor-fg-muted)]" data-testid="text-context-cold-start">
                {context.message}
              </div>
            )}
            {context && context.ready === true && (
              <div className="space-y-4">
                {context.companyExpertise && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--mentor-fg-muted)] mb-1.5">Viren's read on the company</div>
                    <p className="text-sm text-[var(--mentor-fg)] leading-relaxed whitespace-pre-line">{context.companyExpertise}</p>
                  </div>
                )}
                {context.proofSuggestions.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--mentor-fg-muted)] mb-1.5">Proof Favcy thinks they need next</div>
                    <ul className="space-y-2">
                      {context.proofSuggestions.map((p, i) => (
                        <li key={i} className="text-xs text-[var(--mentor-fg)]">
                          <div className="font-semibold">{p.title}</div>
                          {p.whatItProves && <div className="text-[var(--mentor-fg-muted)] mt-0.5">{p.whatItProves}</div>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {context.missionSuggestions.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--mentor-fg-muted)] mb-1.5">Missions Favcy has in mind</div>
                    <ul className="space-y-2">
                      {context.missionSuggestions.map((s, i) => (
                        <li key={i} className="text-xs text-[var(--mentor-fg)]">
                          <div className="font-semibold">{s.title}</div>
                          {s.whatWeWillRun && <div className="text-[var(--mentor-fg-muted)] mt-0.5">{s.whatWeWillRun}</div>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {context.inputsUsed && (
                  <div className="text-[10px] text-[var(--mentor-fg-muted)] italic pt-2 border-t border-[var(--mentor-border)]">
                    {context.inputsUsed}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Look at their missions — explicit reveal so the page reads
          calmly until the mentor opts into deeper context. */}
      {!showMissions ? (
        <button
          onClick={() => setShowMissions(true)}
          className="w-full px-5 py-4 rounded-lg bg-[#FF5C00] text-white text-sm font-semibold hover:bg-[#E55200] transition inline-flex items-center justify-center gap-2"
          data-testid="button-look-at-missions"
        >
          Look at their missions <ArrowRight className="w-4 h-4" />
        </button>
      ) : (
        <section className="space-y-3" data-testid="section-missions">
          <h2 className="text-sm font-semibold text-[var(--mentor-fg)] mb-2">Missions</h2>

          {data.missions.length === 0 && (
            <div className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-6 text-sm text-[var(--mentor-fg-muted)]" data-testid="text-no-missions-empty">
              No open missions for this company yet. We'll surface new missions here as they're posted.
            </div>
          )}

          {submitError && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-xs rounded-lg p-3" data-testid="text-suggested-interest-error">
              {submitError}
            </div>
          )}

          {data.missions.map((m, idx) => {
            if (m.kind === "real") {
              return (
                <Link key={m.id} href={`/mentor/missions/${m.id}`}
                  className="block bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-4 hover:border-[#FF5C00]/40 transition"
                  data-testid={`mission-link-${m.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{m.title}</div>
                      <div className="text-[11px] text-[var(--mentor-fg-muted)] mt-1">
                        {m.authorKind === "viren_draft"
                          ? "Drafted by Viren — pending founder confirmation"
                          : "Authored by founder"}
                        {m.domainTags.length > 0 && ` · ${m.domainTags.slice(0, 3).join(", ")}`}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[var(--mentor-fg-muted)] mt-0.5 flex-shrink-0" />
                  </div>
                </Link>
              );
            }
            // Favcy-suggested mission. The mission is not real yet — the card
            // CANNOT route to /mentor/missions/:id. The CTA below captures
            // mentor appetite (PRD §7.4.3) so the founder gets nudged to
            // post the real mission.
            const alreadyExpressed = data.myExpressedSuggestionTitles.includes(m.title);
            const submitting = submittingTitle === m.title;
            return (
              <div key={`suggestion-${idx}`}
                className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-4"
                data-testid="favcy-suggested-mission">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[var(--mentor-accent)] bg-[#FF5C00]/10 border border-[#FF5C00]/30 rounded px-2 py-0.5">
                    <Sparkles className="w-3 h-3" /> Favcy suggestion
                  </span>
                </div>
                <div className="text-sm font-semibold mb-2">{m.title}</div>
                {m.whatWeWillRun && (
                  <p className="text-xs text-[var(--mentor-fg)] leading-relaxed mb-2">{m.whatWeWillRun}</p>
                )}
                {m.successLooksLike && (
                  <div className="text-[11px] text-[var(--mentor-fg-muted)] mb-1">
                    <span className="font-semibold text-[var(--mentor-fg)]">Success looks like:</span> {m.successLooksLike}
                  </div>
                )}
                {m.whyNow && (
                  <div className="text-[11px] text-[var(--mentor-fg-muted)] mb-3">
                    <span className="font-semibold text-[var(--mentor-fg)]">Why now:</span> {m.whyNow}
                  </div>
                )}

                {/* Appetite CTA — PRD §7.4.3 */}
                <div className="mt-4 pt-3 border-t border-[var(--mentor-border)]">
                  {alreadyExpressed ? (
                    <div className="inline-flex items-center gap-2 text-xs text-[var(--mentor-fg-muted)]" data-testid="text-suggested-interest-expressed">
                      <Check className="w-3.5 h-3.5 text-green-400" />
                      You expressed interest. We'll nudge the founder to post this as a live mission.
                    </div>
                  ) : (
                    <button
                      onClick={() => expressSuggestedInterest(m)}
                      disabled={submitting}
                      className="w-full sm:w-auto px-4 py-2 rounded-md bg-[#FF5C00] text-white text-xs font-semibold hover:bg-[#E55200] transition disabled:opacity-60 disabled:cursor-wait inline-flex items-center justify-center gap-2"
                      data-testid="button-suggested-interest"
                    >
                      {submitting ? "Saving..." : "I'd be keen if the founder posted this"}
                    </button>
                  )}
                </div>

                {data.suggestionSourceNote && (
                  <div className="flex items-start gap-2 text-[11px] text-[var(--mentor-fg-muted)] italic mt-3 pt-3 border-t border-[var(--mentor-border)]">
                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{data.suggestionSourceNote}</span>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      )}

      {/* PRD §7.4.4 — symmetric mission proposal. The mentor can suggest a
          mission Viren never thought of. The form is intentionally below
          the existing mission list so the mentor sees what's already on
          the table before adding noise. */}
      <section className="mt-8 bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-4" data-testid="section-propose-mission">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold">Propose a mission for this company</div>
            <p className="text-xs text-[var(--mentor-fg-muted)] mt-1">See a sprint Favcy hasn't surfaced? Suggest it. Viren will review for fit, and the founder gates the final yes.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!ideas && (
              <button
                onClick={() => loadIdeas(false)}
                disabled={ideasLoading}
                className="text-xs px-3 py-1.5 rounded-md border border-[var(--mentor-border)] hover:border-[#FF5C00]/40 transition inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-wait"
                data-testid="button-get-inspired"
              >
                <Lightbulb className="w-3.5 h-3.5" />
                {ideasLoading ? "Asking Viren..." : "Get inspired"}
              </button>
            )}
            {!showProposeForm && (
              <button
                onClick={() => { setShowProposeForm(true); setProposeOutcome(null); setProposeError(null); }}
                className="text-xs px-3 py-1.5 rounded-md border border-[var(--mentor-border)] hover:border-[#FF5C00]/40 transition"
                data-testid="button-propose-mission-open"
              >Propose</button>
            )}
          </div>
        </div>

        {/* Inspiration cards. Viren-seeded, but they go through the same
            refinement gate when the mentor edits + sends, so no founder
            badge is needed. The cards live above the form so it's clear
            which workflow each path belongs to. */}
        {ideasLoading && !ideas && (
          <div className="mt-4">
            <ProgressLoader active={true} title="Drafting starting points" stages={[
              "Reading Favcy's brief on this company",
              "Checking what's already on the table",
              "Drafting three angles for you to riff on",
            ]} />
          </div>
        )}
        {ideasErr && (
          <div className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300" data-testid="text-ideas-error">
            {ideasErr}
          </div>
        )}
        {ideas && ideas.length > 0 && (
          <div className="mt-4 space-y-3" data-testid="section-inspirations">
            <div className="flex items-center justify-between gap-2">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[var(--mentor-accent)]">
                <Sparkles className="w-3 h-3" /> Three starting points from Viren
                {ideasCached && <span className="text-[var(--mentor-fg-muted)] normal-case tracking-normal text-[10px]">· cached</span>}
              </div>
              <button
                onClick={() => loadIdeas(true)}
                disabled={ideasLoading}
                className="text-[11px] text-[var(--mentor-fg-muted)] hover:text-[var(--mentor-fg)] inline-flex items-center gap-1 disabled:opacity-60"
                data-testid="button-refresh-ideas"
              >
                <RefreshCw className={`w-3 h-3 ${ideasLoading ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {ideas.map((i, idx) => (
                <div key={idx} className="rounded-md border border-[var(--mentor-border)] bg-[var(--mentor-bg)] p-3" data-testid={`inspiration-card-${idx}`}>
                  <div className="text-sm font-semibold">{i.title}</div>
                  <div className="mt-1 text-xs text-[var(--mentor-fg-muted)] leading-relaxed">
                    <span className="font-semibold text-[var(--mentor-fg)]">Problem:</span> {i.problem}
                  </div>
                  <div className="mt-1 text-xs text-[var(--mentor-fg-muted)] leading-relaxed">
                    <span className="font-semibold text-[var(--mentor-fg)]">Approach:</span> {i.approach}
                  </div>
                  <button
                    onClick={() => useIdeaAsStartingPoint(i)}
                    className="mt-3 text-xs px-3 py-1.5 rounded-md bg-[#FF5C00] text-white font-semibold hover:bg-[#E55200] transition inline-flex items-center gap-1.5"
                    data-testid={`button-use-idea-${idx}`}
                  >
                    Use this as a starting point <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-[var(--mentor-fg-muted)] italic">
              Edit before you send — these are seeds, not finished missions. The founder will see your final wording, not Viren's draft.
            </div>
          </div>
        )}

        {proposeOutcome?.kind === "refined" && (
          <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs" data-testid="text-propose-outcome-refined">
            <div className="font-semibold text-emerald-300">Sent to founder for review</div>
            <div className="mt-1 text-[var(--mentor-fg)]">Viren refined your idea to: <em>{proposeOutcome.title}</em>.</div>
            {proposeOutcome.rationale && <div className="mt-1 text-[var(--mentor-fg-muted)]">Why this is a fit: {proposeOutcome.rationale}</div>}
            <div className="mt-2 text-[var(--mentor-fg-muted)]">Track its status in <a href="/mentor/proposals" className="underline">My proposals</a>.</div>
          </div>
        )}
        {proposeOutcome?.kind === "rejected" && (
          <div className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs" data-testid="text-propose-outcome-rejected">
            <div className="font-semibold text-red-300">Viren held this back</div>
            <div className="mt-1 text-[var(--mentor-fg)]">{proposeOutcome.reason}</div>
            <div className="mt-2 text-[var(--mentor-fg-muted)]">Edit your proposal and try again, or browse other openings.</div>
          </div>
        )}

        {showProposeForm && (
          <form onSubmit={submitProposal} className="mt-4 space-y-3" data-testid="form-propose-mission">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[var(--mentor-fg-muted)] mb-1">Mission title</label>
              <input
                type="text" required minLength={6} maxLength={280}
                value={proposeForm.title}
                onChange={(e) => setProposeForm({ ...proposeForm, title: e.target.value })}
                placeholder="e.g. 10 NBFC CFO interviews to validate underwriting wedge"
                className="w-full px-3 py-2 rounded-md bg-[var(--mentor-bg)] border border-[var(--mentor-border)] text-sm focus:border-[#FF5C00] outline-none"
                data-testid="input-propose-title"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[var(--mentor-fg-muted)] mb-1">What problem does this solve?</label>
              <textarea
                required minLength={30} maxLength={4000} rows={3}
                value={proposeForm.problem}
                onChange={(e) => setProposeForm({ ...proposeForm, problem: e.target.value })}
                placeholder="Be specific. What is broken or missing for this company right now? Why does it matter?"
                className="w-full px-3 py-2 rounded-md bg-[var(--mentor-bg)] border border-[var(--mentor-border)] text-sm focus:border-[#FF5C00] outline-none"
                data-testid="input-propose-problem"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[var(--mentor-fg-muted)] mb-1">How would you approach it?</label>
              <textarea
                required minLength={30} maxLength={4000} rows={3}
                value={proposeForm.approach}
                onChange={(e) => setProposeForm({ ...proposeForm, approach: e.target.value })}
                placeholder="What would you actually do over 2-6 weeks? Who would you talk to, what would the output look like?"
                className="w-full px-3 py-2 rounded-md bg-[var(--mentor-bg)] border border-[var(--mentor-border)] text-sm focus:border-[#FF5C00] outline-none"
                data-testid="input-propose-approach"
              />
            </div>
            {proposeError && (
              <div className="text-xs text-red-400" data-testid="text-propose-error">{proposeError}</div>
            )}
            {proposing && (
              <ProgressLoader active={true} title="Sending to Viren" stages={[
                "Pulling Favcy's brief on this company",
                "Viren is reviewing your proposal for fit",
                "Refining the title, problem, and deliverable",
                "Notifying the founder",
              ]} />
            )}
            <div className="flex items-center gap-2">
              <button
                type="submit" disabled={proposing}
                className="px-4 py-2 rounded-md bg-[#FF5C00] text-white text-xs font-semibold hover:bg-[#E55200] transition disabled:opacity-60 disabled:cursor-wait"
                data-testid="button-propose-submit"
              >{proposing ? "Sending to Viren..." : "Send to Viren for review"}</button>
              <button
                type="button" disabled={proposing}
                onClick={() => { setShowProposeForm(false); setProposeError(null); setProposeForm({ title: "", problem: "", approach: "" }); }}
                className="px-3 py-2 rounded-md border border-[var(--mentor-border)] text-xs hover:border-[var(--mentor-fg-muted)] transition disabled:opacity-60"
                data-testid="button-propose-cancel"
              >Cancel</button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
